import { makeStyles } from "tss-react/mui";
import { MiIcon } from "@miplatform/components";
import {
  WORK_ORDER_COLORS_MAP,
  WORK_ORDER_STATUSES_LABELS,
} from "@miplatform/constants";
import {
  IAttempt,
  IAttemptListItemResponseForWorkOrder,
  WorkOrderStatus,
  WorkOrderSubStatus,
} from "@miplatform/types";
import {
  convertTimeToShortHand,
  getWorkOrderBorderColor,
  getWorkOrderDueDateColor,
  getWorkOrderStatus,
} from "@miplatform/utils";
import { mdiCalendarAccountOutline } from "@mdi/js";
import { Info } from "@mibuildweb/components";
import { Box } from "@mui/system";

export const WorkOrderDrawerSubheader = ({
  attempt,
}: WorkOrderDrawerSubHeaderProps) => {
  const { assignedUserName, startDate, assignment, endDate } = attempt;
  const status = getWorkOrderStatus(attempt);
  const { theme, classes } = useStyles({
    status,
    subStatus: assignment?.workOrder?.subStatus,
  });

  return (
    <Box className={classes.root}>
      <Box className={classes.statusContainer}>
        <Info
          text="Work Order Status"
          maxWidth={300}
          subtext={
            <>
              {assignment?.workOrder?.unableToBeAutoAssign && (
                <MiIcon
                  path={mdiCalendarAccountOutline}
                  color={theme.palette.warning.main}
                  size={0.5}
                />
              )}
              {WORK_ORDER_STATUSES_LABELS[status as WorkOrderStatus]}
              {assignment?.workOrder?.subStatus ===
                WorkOrderSubStatus.Released && " / Released"}
            </>
          }
          descriptionColor={WORK_ORDER_COLORS_MAP[status]?.drawerStatus}
          descriptionsProps={{
            sx: { display: "flex", alignItems: "center", gap: 1 },
          }}
        />
      </Box>
      <Box className={classes.infoContainer}>
        <Info
          text="Due Date"
          subtext={
            assignment?.workOrder?.hardDueDate
              ? convertTimeToShortHand(assignment.workOrder.hardDueDate)
              : "-- @ --"
          }
          descriptionColor={getWorkOrderDueDateColor(attempt, theme)}
        />
        <Info
          text="Start Date"
          subtext={startDate ? convertTimeToShortHand(startDate) : "-- @ --"}
        />
        <Info
          text="Complete Date"
          subtext={endDate ? convertTimeToShortHand(endDate) : "-- @ --"}
        />
        <Info text="Assignee" subtext={assignedUserName || "--"} />
      </Box>
    </Box>
  );
};

interface WorkOrderDrawerSubHeaderProps {
  attempt: IAttemptListItemResponseForWorkOrder | IAttempt;
}

const useStyles = makeStyles<{
  status: WorkOrderStatus;
  subStatus?: WorkOrderSubStatus;
}>()((theme, { status, subStatus }) => {
  const {
    drawerStatusBackground,
    drawerBackground,
    drawerBorderColor,
    borderStyle,
  } = WORK_ORDER_COLORS_MAP[status] || {};
  const borderColor =
    getWorkOrderBorderColor(subStatus, theme) || drawerBorderColor;

  return {
    viewContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      height: "100%",
    },
    content: {
      padding: theme.spacing(4),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      overflow: "auto",
      height: "100%",
    },
    tabContainer: {
      borderBottom: `1px solid ${theme.palette.grey.medium}`,
      paddingVertical: theme.spacing(2),
    },
    root: {
      display: "flex",
      gap: theme.spacing(4),
      alignItems: "center",
      borderRadius: theme.spacing(0.5),
      border: `1px ${borderStyle} ${borderColor}`,
      backgroundColor: `${drawerBackground}`,
    },
    statusContainer: {
      padding: theme.spacing(2, 7, 2, 4),
      height: "100%",
      alignContent: "center",
      backgroundColor: `${drawerStatusBackground}`,
      borderRight: `1px solid ${borderColor}`,
    },
    infoContainer: {
      display: "grid",
      gap: theme.spacing(8),
      padding: theme.spacing(1, 3),
      alignItems: "center",
      gridTemplateColumns: "1fr 1fr 1fr 1fr",
      overflow: "hidden",
    },
    timelineContainer: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    tabContent: {
      marginTop: theme.spacing(2),
    },
    workOrderCard: {
      backgroundColor: theme.palette.grey.light,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(1),
      border: `1px solid ${theme.palette.grey.accent}`,
    },
  };
});
